import clsx from "clsx";
import {
  type ForwardedRef,
  forwardRef,
  type JSX,
  type ReactNode,
  useState,
} from "react";
import { type Components, Virtuoso } from "react-virtuoso";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import Checkbox from "common/components/ui/Checkbox";
import {
  DropdownMenuCheckbox,
  DropdownMenuItem,
  DropdownMenuList,
  dropdownMenuListVariants,
} from "common/components/ui/DropdownMenu";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

type FilterItemType = {
  id: string;
} & object;

const DESKTOP_FILTER_ITEM_HEIGHT_PX = 32;

interface SearchInputFilterItemsProps<FilterItem extends FilterItemType> {
  isLoading: boolean;
  getFilteredItems: (lowercaseSearchKeyword: string) => FilterItem[];
  getIsChecked: (item: FilterItem) => boolean;
  onSelectionChange: (
    selectionType: "toggle" | "select",
    item: FilterItem,
  ) => void;
  renderLabel: (item: FilterItem) => string;
  renderLabelPrefixComponent?: (item: FilterItem) => JSX.Element;
  itemCountKey?: keyof FilterItem;
}

type VirtuosoContext = {
  itemCount: number;
  selectItem: (index: number) => void;
};

const VirtuosoItemWrapper: Components<unknown, VirtuosoContext>["Item"] =
  forwardRef(({ children, context, "data-item-index": dataItemIndex }, ref) => {
    const dropdownMenuItem = (
      <DropdownMenuItem
        ref={ref as ForwardedRef<HTMLDivElement>}
        className="min-w-0"
        onSelect={() => {
          context.selectItem(dataItemIndex);
        }}
      >
        {children}
      </DropdownMenuItem>
    );

    if (dataItemIndex === context.itemCount - 1) {
      return <div className="flex flex-col">{dropdownMenuItem}</div>;
    }
    return dropdownMenuItem;
  });

const VirtuosoMobileItemWrapper: Components["Item"] = forwardRef(
  ({ children, ...rest }, ref) => {
    return (
      <div
        ref={ref as ForwardedRef<HTMLDivElement>}
        className="pb-1 last:pb-0"
        {...rest}
      >
        {children}
      </div>
    );
  },
);

export default function SearchInputFilterItems<
  FilterItem extends FilterItemType,
>({
  isLoading,
  getFilteredItems,
  getIsChecked,
  onSelectionChange,
  renderLabel,
  renderLabelPrefixComponent,
  itemCountKey,
}: SearchInputFilterItemsProps<FilterItem>) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = getFilteredItems(searchQuery.toLowerCase());

  const showLoadingSkeletons = isLoading && !filteredItems.length;
  const showEmptyState = !isLoading && !filteredItems.length;
  const desktopListHeightPx =
    Math.min(
      filteredItems.length * DESKTOP_FILTER_ITEM_HEIGHT_PX,
      DESKTOP_FILTER_ITEM_HEIGHT_PX * 6,
    ) + 4;

  const loadingSkeletons = new Array(6).fill(null).map((_, index) => (
    <div className="p-2" key={index.toString()}>
      <Skeleton className="h-4 w-30" />
    </div>
  ));

  let desktopListSection: ReactNode;

  if (showLoadingSkeletons) {
    desktopListSection = (
      <DropdownMenuList>{loadingSkeletons}</DropdownMenuList>
    );
  } else if (showEmptyState) {
    desktopListSection = <DropdownMenuList>{null}</DropdownMenuList>; // DropdownMenuList will handle the empty state
  } else {
    desktopListSection = (
      <div
        className={dropdownMenuListVariants({
          size: "sm",
          isHeightLimited: false,
        })}
      >
        <Virtuoso<FilterItem, VirtuosoContext>
          style={{ height: `${desktopListHeightPx}px` }}
          defaultItemHeight={DESKTOP_FILTER_ITEM_HEIGHT_PX}
          totalCount={filteredItems.length}
          components={{
            Item: VirtuosoItemWrapper as Components<
              FilterItem,
              VirtuosoContext
            >["Item"],
          }}
          className="scrollbar-thumb-black-200 scrollbar-thin"
          context={{
            itemCount: filteredItems.length,
            selectItem: (index) =>
              onSelectionChange("select", filteredItems[index]),
          }}
          itemContent={(index) => {
            const item = filteredItems[index];
            const isChecked = getIsChecked(item);

            return (
              <>
                <DropdownMenuCheckbox
                  isChecked={isChecked}
                  onCheckedChange={() => onSelectionChange("toggle", item)}
                />

                {renderLabelPrefixComponent?.(item)}

                <span className="min-w-0 flex-1 truncate">
                  {renderLabel(item)}
                </span>

                {itemCountKey && typeof item[itemCountKey] === "number" && (
                  <span className="shrink-0 text-black-500 text-xs">
                    {item[itemCountKey]}
                  </span>
                )}
              </>
            );
          }}
        />
      </div>
    );
  }

  let mobileListBody: ReactNode;

  if (showLoadingSkeletons) {
    mobileListBody = (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {loadingSkeletons}
      </div>
    );
  } else if (showEmptyState) {
    mobileListBody = (
      <div className="my-auto flex flex-1 items-center justify-center text-center text-sm text-black-500">
        No results found
      </div>
    );
  } else {
    mobileListBody = (
      <Virtuoso<FilterItem>
        className="w-full flex flex-col min-h-0 flex-1 scrollbar-thin gap-y-1"
        style={{ height: `min(${filteredItems.length * 40}px, 220px)` }}
        data={filteredItems}
        components={{
          Item: VirtuosoMobileItemWrapper as Components<FilterItem>["Item"],
        }}
        itemContent={(_, item) => {
          const isChecked = getIsChecked(item);

          return (
            <button
              type="button"
              onClick={() => onSelectionChange("toggle", item)}
              className="flex w-full min-w-0 items-center gap-x-1.5 p-2 text-left text-button-sm"
            >
              <Checkbox isStyleOnly checked={isChecked} />

              {renderLabelPrefixComponent?.(item)}

              <span className="min-w-0 flex-1 truncate">
                {renderLabel(item)}
              </span>
            </button>
          );
        }}
      />
    );
  }

  if (isDesktop) {
    return (
      <div className="flex min-w-0 w-full flex-col">
        <MenuSearchInput
          sizeVariant="sm"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />

        {desktopListSection}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <SearchInput
        onClear={() => {
          setSearchQuery("");
        }}
        className="mb-3"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
      />

      <div className={clsx("flex flex-col h-72")}>{mobileListBody}</div>
    </div>
  );
}
